import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
	Button,
	TextField,
	AppBar,
	Toolbar,
	Typography,
	Paper,
	CircularProgress,
	Modal,
	Box,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
} from "@mui/material";
import LoadingBar from "react-top-loading-bar";

const AddUserGroup = () => {
	const [showSuccessModal, setShowSuccessModal] = useState(false);
	const navigate = useNavigate();
	const { state } = useLocation();
	const encodedFormId = state?.encodedFormId || "";
	const [userGroupName, setUserGroupName] = useState("");
	const [loadingBarProgress, setLoadingBarProgress] = useState(0);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState("");
	const [openSuccessDialog, setOpenSuccessDialog] = useState(false);

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!userGroupName.trim()) {
			setError("User Group Name is required");
			return;
		}

		setIsSubmitting(true);
		setError("");
		setLoadingBarProgress(30);

		try {
			const apiUrl = process.env.REACT_APP_BASE_URL;
			const response = await fetch(`${apiUrl}/CPanel`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: sessionStorage.getItem("token"),
				},
				body: JSON.stringify({
					userGroupName: userGroupName.trim(),
				}),
			});

			if (response.ok) {
				setOpenSuccessDialog(true);
			} else {
				let errorMsg = "Failed to add user group";
				if (response.status === 400) errorMsg = "Invalid data provided";
				else if (response.status === 401)
					errorMsg = "Unauthorized: Please log in again";
				else if (response.status === 403)
					errorMsg = "Forbidden: You lack permission to add user groups";
				else if (response.status === 404) errorMsg = "Resource not found";
				else if (response.status >= 500)
					errorMsg = "Server error: Please try again later";
				setError(errorMsg);
			}
		} catch (error) {
			console.error("Error adding user group:", error);
			setError("Network error: Unable to connect to the server");
		} finally {
			setLoadingBarProgress(100);
			setIsSubmitting(false);
		}
	};

	const handleCancel = () => {
		setUserGroupName("");
	};

	const handleCloseSuccessDialog = () => {
		setOpenSuccessDialog(false);
		if (!encodedFormId) {
			console.warn(
				"encodedFormId is missing, navigating to default usergroup page"
			);
			navigate("/usergroup/MjE=");
			return;
		}
		navigate(`/usergroup/${encodedFormId}`);
	};

	return (
		<div className="container mx-auto mt-4">
			<LoadingBar
				progress={loadingBarProgress}
				color="rgb(152, 106, 182)"
				height={5}
			/>
			<Box
				sx={{
					backgroundColor: "#0B1F40", // dark navy blue
					padding: "12px 24px",
					boxShadow: 3,
					borderRadius: "4px 4px 0 0",
					margin: "2px auto",
					maxWidth: "900px",
				}}
			>
				<Typography
					variant="h4"
					sx={{ color: "#fff", textAlign: "center", fontWeight: "bold" }}
				>
					Add User Group
				</Typography>{" "}
			</Box>

			<Paper
				elevation={3}
				style={{
					padding: 16,
					marginTop: 2,
					width: "900px",
					margin: "16px auto",
				}}
			>
				<form onSubmit={handleSubmit}>
					<Typography variant="h6" gutterBottom style={{ color: "#127498ff" }}>
						New User Group Details
					</Typography>
					{error && (
						<Typography color="error" sx={{ mb: 2 }}>
							{error}
						</Typography>
					)}
					<TextField
						label="User Group Name"
						variant="outlined"
						fullWidth
						value={userGroupName}
						onChange={(e) => setUserGroupName(e.target.value)}
						required
						sx={{ mb: 3 }}
						error={!!error && !userGroupName.trim()}
						helperText={
							!!error && !userGroupName.trim()
								? "User Group Name is required"
								: ""
						}
					/>
					<Box display="flex" justifyContent="flex-end" gap={1}>
						<Button
							variant="contained"
							color="primary"
							type="submit"
							disabled={isSubmitting}
							startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
						>
							SAVE
						</Button>
						<Button
							variant="contained"
							color="error"
							onClick={handleCancel}
							disabled={isSubmitting}
						>
							RESET
						</Button>
					</Box>
				</form>
			</Paper>

			<Modal
				open={openSuccessDialog}
				onClose={() => setOpenSuccessDialog(false)}
				aria-labelledby="success-modal-title"
				aria-describedby="success-modal-description"
			>
				<Box
					sx={{
						position: "absolute",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
						width: 400,
						bgcolor: "background.paper",
						borderRadius: 2,
						boxShadow: 24,
						overflow: "hidden",
					}}
				>
					{/* Header */}
					<Box sx={{ backgroundColor: "#0B1F40", padding: "12px 16px" }}>
						<Typography
							id="success-modal-title"
							variant="h6"
							sx={{ color: "#fff" }}
						>
							User Guide Added ✅
						</Typography>
					</Box>

					{/* Content */}
					<Box sx={{ padding: "20px" }}>
						<Typography id="success-modal-description" sx={{ mb: 2 }}>
							The User Guide has been added successfully.
						</Typography>

						{/* Buttons */}
						<Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
							<Button
								onClick={() => {
									setOpenSuccessDialog(false);
									navigate(-1); // go back after closing modal
								}}
								variant="contained"
								color="primary"
							>
								OK
							</Button>
						</Box>
					</Box>
				</Box>
			</Modal>
		</div>
	);
};

export default AddUserGroup;
